/**
 * The TV homepage.
 *
 * Same single request as the web homepage — `/api/recommendations/home` — and the same
 * DTO. Only the rendering differs: every card is focusable and the rails are navigated
 * with the remote rather than scrolled with a pointer.
 *
 * Card ids encode their position (`row:col`) rather than the item id. Two rows can
 * legitimately contain the same item — "Because You Watched" and "Newly Added" overlap
 * constantly — and a duplicate id in the focus registry means the second registration
 * overwrites the first, so one of the two cards becomes unreachable.
 */
import { useNavigate } from 'react-router-dom';
import type {
  ContinueWatchingDto,
  HomeRecommendationsDto,
  ProfileRecommendationDto,
} from '@filmodasi/shared';
import { useApi } from '../../../api/hooks';
import { api } from '../../../api/client';
import { Spinner, ErrorBox, Empty } from '../../../components/ui';
import { detailPath } from '../../../catalog/detailPath';
import { TvCard, TvRail } from '../TvShell';

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

function continueSubtitle(entry: ContinueWatchingDto): string {
  const { item, progress, nextEpisode } = entry;
  const episode =
    item.seasonNumber != null && item.episodeNumber != null
      ? `S${String(item.seasonNumber).padStart(2, '0')}B${String(item.episodeNumber).padStart(2, '0')} · `
      : '';
  if (nextEpisode) return `${episode}başlamadı`;
  const remaining =
    progress.durationSec && progress.durationSec > progress.positionSec
      ? `${minutes(progress.durationSec - progress.positionSec)} kaldı`
      : 'devam et';
  return `${episode}${remaining}`;
}

export function TvHomePage() {
  const navigate = useNavigate();
  const home = useApi<HomeRecommendationsDto>('/api/recommendations/home');
  // Its own request for the same reason as on the web: a message somebody sent must not
  // sit behind the recommender's six-hour cache, and a failure here must not take the
  // homepage down with it.
  const fromHousehold = useApi<ProfileRecommendationDto[]>('/api/profile-recommendations');

  if (home.loading && !home.data) return <Spinner label="Öneriler hazırlanıyor…" />;
  if (home.error) return <ErrorBox message={home.error} onRetry={home.reload} />;

  const data = home.data;
  const inbox = fromHousehold.data ?? [];
  if (
    !data ||
    (data.continueWatching.length === 0 && data.rows.length === 0 && inbox.length === 0)
  ) {
    return (
      <Empty>
        Henüz gösterilecek bir şey yok. Bilgisayardan bir kaynak ekleyip eşitleyin, sonra
        birkaç şey izleyin — öneriler kendiliğinden dolar.
      </Empty>
    );
  }

  /*
   * One rail per sender, above everything the algorithm produced.
   *
   * `inbox` arrives newest-first, so the Map's insertion order is already the order the
   * rails should appear in — no sort needed, and none that could disagree with the server's.
   */
  const bySender = new Map<string, ProfileRecommendationDto[]>();
  for (const entry of inbox) {
    const list = bySender.get(entry.senderName);
    if (list) list.push(entry);
    else bySender.set(entry.senderName, [entry]);
  }

  return (
    <>
      {[...bySender].map(([sender, entries]) => (
        <TvRail title={`${sender} senin için önerdi`} key={sender}>
          {entries.map((entry, col) => (
            <TvCard
              key={entry.id}
              id={`home:from:${sender}:${col}`}
              group={`home:from:${sender}`}
              title={entry.item.title}
              // The note where there is one, the year otherwise. On a television the note
              // is the more useful of the two — it is why this card is here at all.
              subtitle={entry.message ?? (entry.item.year ? String(entry.item.year) : null)}
              art={entry.item.poster ?? entry.item.logo}
              poster
              onSelect={() => {
                // Marked read on the way out. Fire-and-forget: a courtesy flag must never
                // sit between the viewer and the film.
                void api(`/api/profile-recommendations/${entry.id}/read`, {
                  method: 'PATCH',
                }).catch(() => {});
                void navigate(detailPath(entry.item));
              }}
            />
          ))}
        </TvRail>
      ))}

      {data.continueWatching.length > 0 && (
        <TvRail title="İzlemeye Devam Et">
          {data.continueWatching.map((entry, col) => (
            <TvCard
              key={entry.item.id}
              id={`home:continue:${col}`}
              group="home:continue"
              title={entry.series?.title ?? entry.item.title}
              subtitle={continueSubtitle(entry)}
              art={
                entry.item.poster ??
                entry.item.logo ??
                entry.series?.poster ??
                entry.series?.logo
              }
              // Zero for a not-yet-started next episode: the bar exists to keep card
              // heights equal, not to claim progress nobody made.
              progressPercent={entry.nextEpisode ? 0 : entry.progress.percent}
              onSelect={() => navigate(detailPath(entry.item))}
            />
          ))}
        </TvRail>
      )}

      {data.rows.map((row, rowIndex) => (
        <TvRail title={row.title} key={row.key}>
          {row.items.map((item, col) => (
            <TvCard
              key={item.id}
              id={`home:${rowIndex}:${col}`}
              group={`home:${row.key}`}
              title={item.title}
              subtitle={item.year ? String(item.year) : null}
              art={item.poster ?? item.logo}
              onSelect={() => navigate(detailPath(item))}
            />
          ))}
        </TvRail>
      ))}
    </>
  );
}
